import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Mail, Save, Send, Loader2, Server, Lock, User, AtSign, SendHorizonal } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SmtpPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [config, setConfig] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Watizap',
    use_tls: true
  });
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/smtp`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setConfig({
            host: data.host || '',
            port: data.port || 587,
            username: data.username || '',
            password: data.password || '',
            from_email: data.from_email || '',
            from_name: data.from_name || 'Watizap',
            use_tls: data.use_tls !== false
          });
          setHasExistingConfig(true);
        }
      }
    } catch (error) {
      console.error('Error fetching SMTP config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!config.host || !config.port || !config.username || !config.from_email) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (!hasExistingConfig && !config.password) {
      toast.error('Senha é obrigatória para nova configuração');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/smtp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao salvar');
      }

      toast.success('Configuração SMTP salva com sucesso!');
      setHasExistingConfig(true);
      setConfig(prev => ({ ...prev, password: '' }));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Digite um email para enviar o teste');
      return;
    }

    if (!hasExistingConfig) {
      toast.error('Salve a configuração SMTP primeiro');
      return;
    }

    setTesting(true);
    
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/smtp/test`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ to_email: testEmail }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao testar');
      }

      toast.success(`Email de teste enviado para ${testEmail}!`);
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error('Timeout: O servidor não respondeu em 15 segundos. Verifique as configurações.');
      } else {
        toast.error(error.message || 'Erro ao enviar teste');
      }
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="smtp-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Configuração SMTP</h1>
        <p className="text-muted-foreground">
          Configure o servidor de email para envio de notificações e recuperação de senha
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Servidor de Email
          </CardTitle>
          <CardDescription>
            Configure as credenciais do seu servidor SMTP
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Host */}
              <div className="space-y-2">
                <Label htmlFor="host" className="flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Servidor SMTP *
                </Label>
                <Input
                  id="host"
                  placeholder="smtp.gmail.com"
                  value={config.host}
                  onChange={(e) => setConfig({ ...config, host: e.target.value })}
                  required
                />
              </div>

              {/* Port */}
              <div className="space-y-2">
                <Label htmlFor="port">Porta *</Label>
                <Input
                  id="port"
                  type="number"
                  placeholder="587"
                  value={config.port}
                  onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) || 587 })}
                  required
                />
              </div>

              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="username" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Usuário *
                </Label>
                <Input
                  id="username"
                  placeholder="seu@email.com"
                  value={config.username}
                  onChange={(e) => setConfig({ ...config, username: e.target.value })}
                  required
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Senha {hasExistingConfig ? '(deixe em branco para manter)' : '*'}
                </Label>
                <Input
                  id="password"
                  type="text"
                  placeholder="senha do smtp"
                  value={config.password}
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  required={!hasExistingConfig}
                />
              </div>

              {/* From Email */}
              <div className="space-y-2">
                <Label htmlFor="from_email" className="flex items-center gap-2">
                  <AtSign className="w-4 h-4" />
                  Email de Envio *
                </Label>
                <Input
                  id="from_email"
                  type="email"
                  placeholder="noreply@seudominio.com"
                  value={config.from_email}
                  onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
                  required
                />
              </div>

              {/* From Name - aligned with From Email */}
              <div className="space-y-2">
                <Label htmlFor="from_name" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Nome do Remetente
                </Label>
                <Input
                  id="from_name"
                  placeholder="Watizap"
                  value={config.from_name}
                  onChange={(e) => setConfig({ ...config, from_name: e.target.value })}
                />
              </div>
            </div>

            {/* TLS Toggle */}
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="use_tls" className="font-medium">Usar TLS/STARTTLS</Label>
                <p className="text-sm text-muted-foreground">
                  Recomendado para conexões seguras (porta 587)
                </p>
              </div>
              <Switch
                id="use_tls"
                checked={config.use_tls}
                onCheckedChange={(checked) => setConfig({ ...config, use_tls: checked })}
              />
            </div>

            {/* Test Email Section */}
            <div className="p-4 border rounded-lg space-y-3 bg-secondary/10">
              <div className="flex items-center gap-2">
                <SendHorizonal className="w-5 h-5" />
                <Label className="font-medium text-base">Testar Configurações</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Envie um email de teste para verificar se as configurações estão corretas.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="Digite o email para receber o teste"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleTest}
                  disabled={testing || !hasExistingConfig || !testEmail}
                >
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar Teste
                    </>
                  )}
                </Button>
              </div>
              {!hasExistingConfig && (
                <p className="text-sm text-amber-700 font-bold">
                  ⚠️ Salve a configuração primeiro para habilitar o teste.
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Configuração
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
